import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeClosed, Check } from 'lucide-react';

const roles = [
  { value: 'Admin', label: 'Admin' },
  { value: 'HR', label: 'HR' },
  { value: 'Staff', label: 'Staff' },
];

const EmployeeRegistrationModal = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: ""
  });
  const [errors, setErrors] = useState({});
  const { state } = useAuth();
  const token = state.token;

  // State for custom dropdown
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);

  // State for password criteria
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ email: "", password: "", role: "" });
      setErrors({});
      setPasswordCriteria({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      });
      setShowDropdown(false);
      setShowPassword(false);
    }
  }, [isOpen]);

  // Update password criteria as password changes
  useEffect(() => {
    setPasswordCriteria({
      length: formData.password.length >= 8,
      uppercase: /[A-Z]/.test(formData.password),
      lowercase: /[a-z]/.test(formData.password),
      number: /[0-9]/.test(formData.password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password),
    });
  }, [formData.password]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Password validation: check against our criteria
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (
      !passwordCriteria.length ||
      !passwordCriteria.uppercase ||
      !passwordCriteria.lowercase ||
      !passwordCriteria.number ||
      !passwordCriteria.special
    ) {
      newErrors.password = "Password does not meet all requirements";
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = "Role selection is required";
    }

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    try {
      setIsSubmitting(true);
      const response = await apiService.post("/api/auth/register-staffs", token, formData);
      console.log('Employee registered:', response.data);
      onClose();  // Close modal after success
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ submit: error.message || "Registration failed" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <div className="p-8">
            <h2 className="text-3xl font-bold text-[#732570] mb-4">
              Register New Employee
            </h2>
            <p className="text-gray-600 mb-6">
              Add a new team member to your organization
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="employee@company.com"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password Field with Visibility Toggle */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a strong password"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeClosed size={20} /> : <Eye size={20} />}
                </button>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
                {/* Password Checklist */}
                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Password must have:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm">
                      {passwordCriteria.length ? (
                        <Check size={16} className="mr-2 text-primary-600" />
                      ) : (
                        <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                          <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        </div>
                      )}
                      <span className={passwordCriteria.length ? "text-primary-600" : "text-gray-600"}>
                        At least 8 characters
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      {passwordCriteria.uppercase ? (
                        <Check size={16} className="mr-2 text-primary-600" />
                      ) : (
                        <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                          <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        </div>
                      )}
                      <span className={passwordCriteria.uppercase ? "text-primary-600" : "text-gray-600"}>
                        At least one uppercase letter
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      {passwordCriteria.lowercase ? (
                        <Check size={16} className="mr-2 text-primary-600" />
                      ) : (
                        <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                          <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        </div>
                      )}
                      <span className={passwordCriteria.lowercase ? "text-primary-600" : "text-gray-600"}>
                        At least one lowercase letter
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      {passwordCriteria.number ? (
                        <Check size={16} className="mr-2 text-primary-600" />
                      ) : (
                        <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                          <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        </div>
                      )}
                      <span className={passwordCriteria.number ? "text-primary-600" : "text-gray-600"}>
                        At least one number
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      {passwordCriteria.special ? (
                        <Check size={16} className="mr-2 text-primary-600" />
                      ) : (
                        <div className="w-4 h-4 mr-2 rounded-full border border-gray-300 flex items-center justify-center">
                          <span className="block w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        </div>
                      )}
                      <span className={passwordCriteria.special ? "text-primary-600" : "text-gray-600"}>
                        At least one special character
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Custom Dropdown for Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#732570] text-left"
                  >
                    {formData.role
                      ? roles.find((r) => r.value === formData.role)?.label
                      : "Select Role"}
                  </button>
                  {showDropdown && (
                    <div className="absolute mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-10">
                      {roles.map((role) => (
                        <div
                          key={role.value}
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, role: role.value }));
                            setShowDropdown(false);
                          }}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                        >
                          {role.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.role && (
                  <p className="text-red-500 text-xs mt-1">{errors.role}</p>
                )}
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-2 rounded-md">
                  {errors.submit}
                </div>
              )}

              <div className="flex justify-end space-x-4 mt-8">
                <button 
                  type="button" 
                  onClick={onClose}
                  className="px-4 py-2 text-[#732570] border border-[#732570] rounded-md hover:bg-[#732570]/10 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#732570] text-white rounded-md hover:bg-[#5c1e5c] transition-colors flex items-center justify-center"
                >
                  {isSubmitting && (
                    <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  )}
                  {isSubmitting ? "Registering..." : "Register Employee"}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmployeeRegistrationModal;
